import type { CartItem } from '../types';

/** Raw cart JSON from API (camelCase or snake_case). */
export function mapCartResponseToItems(raw: Record<string, unknown> | null | undefined): {
  items: CartItem[];
  cartId: number | null;
  expiresAtIso: string | null;
  eventId: string | null;
} {
  if (!raw || typeof raw !== 'object') {
    return { items: [], cartId: null, expiresAtIso: null, eventId: null };
  }

  const cartId = (raw.id as number) ?? null;
  const expiresAtIso =
    (raw.expiresAt as string) ?? (raw.expires_at as string) ?? null;
  const eventIdRaw = (raw.eventId as number) ?? (raw.event_id as number);
  const eventId = eventIdRaw != null ? String(eventIdRaw) : null;
  const eventTitle = (raw.eventTitle as string) ?? (raw.event_title as string) ?? '';
  const eventDateRaw = (raw.eventDate as string) ?? (raw.event_date as string);
  const eventDate =
    eventDateRaw && typeof eventDateRaw === 'string'
      ? eventDateRaw.split('T')[0]
      : '';

  const itemsArr = (raw.items as Record<string, unknown>[]) ?? [];
  const items: CartItem[] = itemsArr.map((row) => {
    const seatCategoryId =
      (row.seatCategoryId as number | null) ?? (row.seat_category_id as number | null);
    const seatIds = (row.seatIds as number[]) ?? (row.seat_ids as number[]) ?? undefined;
    const categoryName =
      (row.categoryName as string) ?? (row.category_name as string) ?? 'Tier';
    const groupName = (row.activityGroupName as string) ?? (row.activity_group_name as string) ?? undefined;
    const groupIdRaw = (row.activityGroupId as number) ?? (row.activity_group_id as number);
    const activitiesRaw = (row.activities as Record<string, unknown>[]) ?? [];
    const activities = activitiesRaw.map((a) => ({
      activityId: ((a.activityId ?? a.activity_id) as number | undefined) ?? null,
      activityName: String(a.activityName ?? a.activity_name ?? ''),
      activitySlotId: ((a.activitySlotId ?? a.activity_slot_id) as number | undefined) ?? null,
      startsAt: String(a.startsAt ?? a.starts_at ?? ''),
      endsAt: String(a.endsAt ?? a.ends_at ?? ''),
    }));
    const unitPrice = Number(
      (row.unitPrice as string | number) ?? (row.unit_price as string | number) ?? 0
    );
    const quantity = (row.quantity as number) ?? 1;
    const cartItemId = (row.id as number) ?? undefined;
    const occurrenceIdRaw = (row.occurrenceId as number) ?? (row.occurrence_id as number);
    const occurrenceStartsAt =
      (row.occurrenceStartsAt as string) ?? (row.occurrence_starts_at as string) ?? undefined;
    const occurrenceLabel =
      (row.occurrenceLabel as string) ?? (row.occurrence_label as string) ?? undefined;
    const isPass = Boolean(row.isPass ?? row.is_pass);

    return {
      cartId: cartId ?? undefined,
      cartItemId,
      eventId: eventId ?? '',
      eventTitle,
      eventDate,
      // Activity lines have no ticket tier
      tierId: seatCategoryId != null ? String(seatCategoryId) : '',
      tierName: categoryName,
      quantity,
      unitPrice,
      seatIds: seatIds?.length ? seatIds : undefined,
      occurrenceId: occurrenceIdRaw != null ? String(occurrenceIdRaw) : undefined,
      occurrenceStartsAt,
      occurrenceLabel,
      isPass: isPass || undefined,
      ...(activities.length
        ? {
            activities,
            activityGroupId: groupIdRaw != null ? String(groupIdRaw) : undefined,
            activityGroupName: groupName,
          }
        : {}),
    };
  });

  return { items, cartId, expiresAtIso, eventId };
}

export function persistCartSnapshot(
  items: CartItem[],
  eventId: string | null,
  expiresAtIso: string | null
): void {
  try {
    if (items.length === 0) {
      localStorage.removeItem('zoniq_cart');
      localStorage.removeItem('zoniq_cart_event_id');
      localStorage.removeItem('zoniq_session_time');
      return;
    }
    localStorage.setItem('zoniq_cart', JSON.stringify(items));
    if (eventId) localStorage.setItem('zoniq_cart_event_id', eventId);
    if (expiresAtIso) {
      const expSec = Math.floor(new Date(expiresAtIso).getTime() / 1000);
      if (!Number.isNaN(expSec)) {
        localStorage.setItem('zoniq_session_time', String(expSec));
      }
    }
  } catch {
    /* ignore quota */
  }
}
